import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ProjectDocumentsTab from './ProjectDocumentsTab';

import { userAPI } from '../UserAPI';
import { useTranslation } from 'react-i18next';
import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { forgetProjects } from '../cbos/selectedProject';
import { Asset, LegalPerson, EntityQuery, Project, Query, is, Building, Floor, Role, RoleType, Customer, Site, and, or, Partnership, from, Entitlement, DocumentDetails } from '@the-real-insight/tri-model';
import { useAdministrationAPI } from '../AdministrationAPI';
import AddressPicker from '../components/AddressPicker';
import LegalPersonPicker from '../components/LegalPersonPicker';
import AssetList from '../components/AssetList';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDrag, useDrop } from 'react-dnd';
import TreeView, { flattenTree } from 'react-accessible-treeview';
import RoleList from '../components/RoleList';
import truncate from 'truncate';
import { useUrlPageState } from '../utilities/dataTablePagination';
import { buildingsForSite, entityIdAsString, queryBuildingsForSite } from '../utilities/buildingLookup';
import InvoicePanel from '../components/InvoicePanel';
import { useAuthorization } from '../hooks/Autorization';
import dayjs from 'dayjs';
import { useModalContext } from '../components/ModalProvider';
import { legalPersonAddressFields, legalPersonCityLine, legalPersonCompanyName, legalPersonStreetLine } from '../utilities/legalPersonDisplay';
import { buildingFullAddressLine, buildingPostalCityLine, buildingStreetLine } from '../utilities/buildingFlatAddress';
import { assetMatchesProjectArchiveScope, projectArchiveSiteAddressKeys, type ProjectArchiveScope } from './projectArchiveScope';
import { useGuideAction } from '../guide/useGuideAction';

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    CalendarCog,
    Calendar as CalendarIcon,
    ChevronRight,
    ChevronsUpDown,
    Download,
    Edit,
    FolderPlus,
    Globe,
    Loader2,
    MapPin,
    Plus,
    Save,
    Star,
    Trash2,
    UserCheck,
    UserPlus,
    Users,
    X,
} from 'lucide-react';
import { entityHref } from '../entities/entityNavigation';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { DataTable } from '@/components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { documentIdsOf } from "../utilities/documentReferences";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

/** Anzeigeformat der Datumsfelder — entspricht dem Format des abgelösten DatePickers. */
// Anzeige folgt der Oberflächensprache: deutsches Format in der deutschen UI.
const DATE_DISPLAY_FORMAT = 'DD.MM.YYYY';

/** Site row or first linked building — for project header above tabs. */
function siteAddressLineForProjectHeader(site: any): string {
    if (!site) {
        return '';
    }
    const fromSite =
        buildingFullAddressLine(site) ||
        [buildingStreetLine(site), buildingPostalCityLine(site)].filter(Boolean).join(', ');
    if (fromSite.trim()) {
        return fromSite.trim();
    }
    const b = site.buildings?.[0];
    return b ? buildingFullAddressLine(b) : '';
}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                : sorted === 'desc'
                    ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

/** Leerzustand einer Tabelle: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/** Datumsfeld: Kalender (react-day-picker) in einem Popover, Anzeige über dayjs. */
function DateField({
    id,
    label,
    value,
    disabled,
    onSelectDate,
}: {
    id: string;
    label: string;
    value?: Date;
    disabled?: boolean;
    onSelectDate: (date: Date) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selected = value && !Number.isNaN(value.getTime()) ? value : undefined;

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor={id}>{label}</Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        className="w-full justify-between font-normal"
                    >
                        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                            {selected ? dayjs(selected).format(DATE_DISPLAY_FORMAT) : t('global.pleaseSelect')}
                        </span>
                        <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={selected}
                        defaultMonth={selected}
                        onSelect={(date) => {
                            if (date) {
                                onSelectDate(date);
                            }
                            setOpen(false);
                        }}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

interface Properties {
}


enum BuildingPart {
    wholeBuilding = 'wholeBuilding',
    partOfBuilding = 'partOfBuilding'
}

/**
 * Like queryBuildingsForSite but get-or-create (POST /buildings/ensure): used by
 * the attach flow (linking a building to a new site), where a missing building
 * should be minted rather than left unresolved. Returns [building] or [].
 */
/**
 * The building of a site, created where it does not exist yet.
 *
 * `POST /buildings/ensure` mints a Building for any German address, so a failure here is a failure,
 * not a reason to guess. It used to be one: every error fell through to the exact-string lookups
 * below, which find nothing for a freshly picked address, and the caller then created a site with
 * no building and said nothing — the "Objekt/Fläche" cell stayed empty, and with it the only route
 * into the building where floor plans and equipment live.
 *
 * So only a 404 falls through — that is the server genuinely not offering the endpoint, and then
 * the lookups are the best that can be done. A server error is retried once, because it is usually
 * a moment rather than a state, and then raised.
 */
async function ensureBuildingsForSite(
    site: {
        street?: string;
        streetNumber?: string;
        postalCode?: string;
        city?: string;
        country?: unknown;
        lat?: number;
        lon?: number;
    },
    /** Recorded on the building by the server, so the link survives a reload. */
    siteId?: string,
): Promise<any[]> {
    const street = String(site.street ?? '').trim();
    const streetNumber = String(site.streetNumber ?? '').trim();
    const postalCode = String(site.postalCode ?? '').trim();
    const city = String(site.city ?? '').trim();

    if (!street || !postalCode || !city) {
        return [];
    }

    const ensure = () =>
        userAPI.ensureBuilding(
            {
                ...site,
                street,
                streetNumber,
                postalCode,
                city,
            },
            siteId,
        );

    try {
        const building = await ensure();
        if (building) {
            return [building];
        }
    } catch (error: any) {
        // `ensureBuilding` answers a 404 with null rather than throwing, so anything caught here is
        // a server or network failure.
        const status = Number(String(error?.message ?? '').match(/failed: (\d+)/)?.[1]);

        if (status && status < 500) {
            throw error;
        }

        try {
            const building = await ensure();
            if (building) {
                return [building];
            }
        } catch (retryError) {
            console.error('ensureBuilding failed twice', retryError);
            throw retryError;
        }
    }

    // Fallback: if /buildings/ensure is unavailable or returns 404, try direct lookup.
    // This ensures existing buildings can be found even when the server endpoint is missing.
    const byFlat = await userAPI.query(
        from(Building.type).where(
            and(
                is('street').equal(street),
                is('streetNumber').equal(streetNumber),
                is('postalCode').equal(postalCode),
                is('city').equal(city),
            ),
        ),
    );
    if (byFlat.length > 0) {
        return byFlat;
    }

    const byNested = await userAPI.query(
        from(Building.type).where(
            and(
                is('address.street').equal(street),
                is('address.streetNumber').equal(streetNumber),
                is('address.postalCode').equal(postalCode),
                is('address.city').equal(city),
            ),
        ),
    );
    if (byNested.length > 0) {
        return byNested;
    }

    // City-agnostic fallback: handles city naming variance (e.g., "Frankfurt" vs "Frankfurt am Main")
    const byFlatNoCity = await userAPI.query(
        from(Building.type).where(
            and(
                is('street').equal(street),
                is('streetNumber').equal(streetNumber),
                is('postalCode').equal(postalCode),
            ),
        ),
    );
    if (byFlatNoCity.length > 0) {
        return byFlatNoCity;
    }

    return userAPI.query(
        from(Building.type).where(
            and(
                is('address.street').equal(street),
                is('address.streetNumber').equal(streetNumber),
                is('address.postalCode').equal(postalCode),
            ),
        ),
    );
}

function oneOrManyEqualFilter(field: string, values: string[]) {
    const uniqueValues = Array.from(new Set(values.filter((value) => value.length > 0)));
    return uniqueValues.length === 1
        ? is(field).equal(uniqueValues[0])
        : or(...uniqueValues.map((value) => is(field).equal(value)));
}

function oneOrManyReferenceFilters(fields: string[], values: Set<string>) {
    const uniqueValues = Array.from(values).filter((value) => value.length > 0);
    return uniqueValues.length === 0
        ? []
        : fields.map((field) => oneOrManyEqualFilter(field, uniqueValues));
}

function siteAddressFilter(site: any) {
    return and(
        is('street').equal(site.street),
        is('streetNumber').equal(site.streetNumber),
        is('postalCode').equal(site.postalCode)
    );
}

function siteBuildingIds(sites: any[]): string[] {
    return (sites || []).flatMap((site: any) =>
        (site?.buildings || [])
            .map((building: any) => entityIdAsString(building?._id ?? building?.id))
            .filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0)
    );
}

async function projectArchiveBuildingsForSites(sites: any[]): Promise<any[]> {
    const buildingsById = new Map<string, any>();
    const addBuilding = (building: any) => {
        const id = entityIdAsString(building?._id ?? building?.id);
        if (id) {
            buildingsById.set(id, building);
        }
    };

    (sites || []).forEach((site: any) => (site?.buildings || []).forEach(addBuilding));

    const sitesMissingBuildings = (sites || []).filter((site: any) => siteBuildingIds([site]).length === 0);
    const resolvedBuildings = await Promise.all(
        sitesMissingBuildings.map((site: any) =>
            withTimeoutPromise(buildingsForSite(site), BUILDING_QUERY_TIMEOUT_MS, []).catch((error) => {
                console.error('Failed to resolve buildings for project archive site scope', error);
                return [];
            })
        )
    );
    resolvedBuildings.flat().forEach(addBuilding);

    return Array.from(buildingsById.values());
}

function projectArchiveAssetCandidateFilter(siteFilters: any[], scope: ProjectArchiveScope) {
    const filters = [
        ...(siteFilters.length === 0 ? [] : [siteFilters.length === 1 ? siteFilters[0] : or(...siteFilters)]),
        ...oneOrManyReferenceFilters(['project', 'projectId', 'projects', 'projectIds', '_project'], scope.projectIds),
        ...oneOrManyReferenceFilters(['site', 'siteId', 'sites', 'siteIds', '_site'], scope.siteIds),
        ...oneOrManyReferenceFilters(['building', 'buildingId', 'buildings', 'buildingIds', '_building'], scope.buildingIds),
        ...oneOrManyReferenceFilters(['floor', 'floorId', 'floors', 'floorIds', '_floor'], scope.floorIds),
    ];

    return filters.length === 1 ? filters[0] : or(...filters);
}

/** Avoid hanging forever if /query never settles (e.g. network or server stall). */
function withTimeoutPromise<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((resolve) => {
            setTimeout(() => resolve(fallback), ms);
        }),
    ]);
}

/** Applied to Site and Building /query calls so a stalled server cannot block the Liegenschaften spinner forever. */
const PROJECT_SITE_QUERY_TIMEOUT_MS = 30_000;
/** Alias — some branches still reference this name in `withTimeoutPromise` for the Building query. */
const BUILDING_QUERY_TIMEOUT_MS = PROJECT_SITE_QUERY_TIMEOUT_MS;

export default function ProjectPanel(_properties: Properties) {
    const { t } = useTranslation();
    const projectListPagination = useUrlPageState('projectListPage');
    const projectSitesPagination = useUrlPageState('projectSitesPage');
    const projectPartnershipsPagination = useUrlPageState('projectPartnershipsPage');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    /*
     * react-router baut `setSearchParams` bei jeder Adressänderung neu. Wer es in die Abhängigkeiten
     * eines Callbacks nimmt, der wiederum im Projekte-Loader steht, lädt bei jedem Blättern neu —
     * genau die Schleife aus #1186. Der Loader greift daher über diese Referenz zu.
     */
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;
    const [authentication] = useAtom(authenticationAtom) as any;
    const { loggedInUser: _loggedInUser, isLoggedInUserEntitled } = useAuthorization();
    const { getProjects: _getProjects, createProject, updateProject, deleteProject, deleteProjectEntitlement: _deleteProjectEntitlement } = useAdministrationAPI();
    const [projects, setProjects] = useState([]) as any;
    const [selectedProject, setSelectedProject] = useState() as any;
    const [_projectOwner, setProjectOwner] = useState() as any;
    const [partnerships, setPartnerships] = useState([]) as any;
    const [emailPrimaryContact, setEmailPrimaryContact] = useState('') as any;
    const [emailPrimaryContactTouched, setEmailPrimaryContactTouched] = useState(false);
    const [documents, setDocuments] = useState([]) as any;
    const [_deletedDocuments, _setDeletedDocuments] = useState([]) as any;
    const [order, setOrder] = useState() as any;
    const [loadingProjects, setLoadingProjects] = useState(false) as any;
    const [loadingSites, setLoadingSites] = useState(false) as any;
    const [loadingEntitlements, setLoadingEntitlements] = useState(false) as any;
    const [tab, setTab] = useState(searchParams.get("tab") || 'project') as any;
    /*
     * Nur diese beiden Parameter dürfen Daten nachladen. Wer stattdessen `searchParams` als
     * Ganzes beobachtet, lädt bei jedem Blättern neu (`projectListPage`) — und die Projektliste
     * sprang so bei jedem Klick auf „weiter“ zurück auf Seite 1 (#1186).
     */
    const projectIdInUrl = searchParams.get('project');
    const siteIdInUrl = searchParams.get('site');
    const [_documentVisibility, _setDocumentVisibility] = useState(['_self']);
    const [objectSelectDialogOpen, setObjectSelectDialogOpen] = useState(false);
    /** Last address confirmed in Objektauswahl; avoids losing selection when SearchBox fires while clicking Hinzufügen */
    const objectSelectionAddressRef = useRef<any>(null);
    const [partnerSelectDialogOpen, setPartnerSelectDialogOpen] = useState(false);
    const [address, setAddress] = useState() as any;
    const [client, setClient] = useState() as any;
    const [_selectedEntitlement, setSelectedEntitlement] = useState() as any;
    const [newSupplier, setNewSupplier] = useState() as any;
    const [ownEntitlement, _setOwnEntitlement] = useState() as any;
    const [buildingPart, setBuildingPart] = useState(BuildingPart.wholeBuilding) as any;
    const [_building, setBuilding] = useState([]) as any;
    const [floors, setFloors] = useState([]) as any;
    const [floorSelectOpen, setFloorSelectOpen] = useState(false);
    const [selectedFloors, setSelectedFloors] = useState([]) as any;
    const [siteName, setSiteName] = useState('') as any;
    const [siteNameTouched, setSiteNameTouched] = useState(false);
    const [floorName, setFloorName] = useState('') as any;
    const [floorNameTouched, setFloorNameTouched] = useState(false);
    const [selectedSite, setSelectedSite] = useState() as any;
    const [expandedIds, _setExpandedIds] = useState([]) as any;
    const [_roles, setRoles] = useState([]) as any;
    const [sites, setSites] = useState([]) as any;
    /** Which site is currently being repaired, so its button can say so and cannot be pressed twice. */
    const [linkingSiteId, setLinkingSiteId] = useState<string | null>(null);
    const [siteTreeData, setSiteTreeData] = useState(flattenTree({ name: '', children: [] })) as any;
    const [partnerTreeData, setPartnerTreeData] = useState(flattenTree({ name: '', children: [] })) as any;
    const [projectModified, setProjectModified] = useState(false);
    const [downloadingProject, setDownloadingProject] = useState(false);
    const { showDeleteDialog, pushError, pushSuccess, pushMessage } = useModalContext() as any;

    const buildSiteTree = useCallback(async (sites: any, roles: Role[]) => {
        const projectName = selectedProject?.name ?? '';
        const generalNode = {
            name: 'Allgemein',
            children: roles.filter((role: Role) => role.type === RoleType.role)
                .map((role: Role) => ({
                    name: role.name,
                    children: [],
                    metadata: role,
                })),
            metadata: {
                nodeType: 'global',
                name: 'Allgemein',
            }
        };
        const projectNode = {
            name: projectName,
            children: [] as any,
            metadata: {
                nodeType: 'project',
                name: projectName,
            }
        };
        const siteMap = sites.reduce((map: any, site: any) => {
            map[site._id] = {
                name: `${site.name}`,
                children: [],
                metadata: { ...site, nodeType: 'site' }
            }

            return map;
        }, {});

        const roleMap: any = {};

        roles.filter((role: Role) => role.type === RoleType.parameterizedRole)
            .forEach((role: Role) => {
                if (!!role._id && !!role.parameterizedBy['Site'] && !!siteMap[role.parameterizedBy['Site']._id]) {
                    const roleNode = {
                        name: `${role.name}`,
                        children: [],
                        metadata: { ...role, nodeType: 'role' }
                    };
                    roleMap[role._id] = roleNode;
                    siteMap[role.parameterizedBy['Site']._id].children.push(roleNode);
                } else if (!!role._id && !!role.parameterizedBy['Project']) {
                    const roleNode = {
                        name: `${role.name}`,
                        children: [],
                        metadata: { ...role, nodeType: 'role' }
                    };
                    roleMap[role._id] = roleNode;
                    projectNode.children.push(roleNode);
                }
            });

        // TODO Possibly server-side function

        for (const role of roles.filter((role: Role) => role.type === RoleType.partnerDelegation)) {
            role.delegate = await userAPI.queryOne(from(LegalPerson.type).where(is('_id').equal(role.delegate)));
            role.issuer = await userAPI.queryOne(from(LegalPerson.type).where(is('_id').equal(role.issuer)));

            if (!!role._id && !!roleMap[role.principal]) {
                roleMap[role.principal].children.push({
                    name: `${role.name}`,
                    children: [],
                    metadata: { ...role, nodeType: 'partner' }
                });
            }
        }

        const flatTree = flattenTree({
            name: '',
            children: [generalNode, projectNode, ...Object.values(siteMap) as any]
        });

        return flatTree;
    }, [selectedProject?.name]);

    const refreshRoles = useCallback(async (sites: Site[]) => {
        const newRoles = await userAPI.query(from(Role.type));

        setRoles(newRoles);
        setSiteTreeData(await buildSiteTree(sites, newRoles));
    }, [buildSiteTree]);

    const buildPartnerTree = useCallback((partnerships: Partnership[]) => {
        return flattenTree({
            name: "",
            children: partnerships.map((partnership: Partnership) => ({
                // @ts-ignore
                name: legalPersonCompanyName(partnership.supplier as any),
                metadata: {
                    customer: partnership.customer,
                    supplier: partnership.supplier,
                } as any
            })),
        });
    }, []);

    const addRoleToProject = async (role: any) => {
        console.log(role, selectedProject);

        const result = await userAPI.createRole(authentication.customer, Role.fromTemplate(role, 'Project', selectedProject, selectedProject.name));

        console.log(result);

        refreshRoles(sites);
    };

    const addRoleToSite = async (role: any, site: any) => {
        console.log(role, site);

        const result = await userAPI.createRole(authentication.customer, Role.fromTemplate(role, 'Site', site, site.name));

        console.log(result);

        refreshRoles(sites);
    };

    const addPartnerToRole = async (partner: Partnership, role: any) => {
        const result = await userAPI.createRole(authentication.customer, Role.partnerDelegation(role,
            new Customer(partner.customer),
            new Customer(partner.supplier)));

        console.log(result);

        refreshRoles(sites);
    };

    const removeRole = async (role: any) => {
        await userAPI.deleteRole(authentication.customer, role);

        refreshRoles(sites);
    };

    interface DraggableProperties {
        children?: any;
        item?: any;
        type?: any;
    }

    const Draggable = (properties: DraggableProperties) => {
        const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
            type: properties.type || 'BOX',
            item: properties.item,
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        }))

        return <>
            <div ref={dragPreview} className={cn(isDragging && 'opacity-50')}>
                <div ref={drag} >
                    {properties && properties.children
                        ?
                        (properties.children)
                        :
                        <></>
                    }
                </div>
            </div>
        </>;
    }

    interface DroppableProperties {
        accept?: any;
        children?: any;
        theme?: any;
        onDrop: (payload: any) => void
    }

    const Droppable = (properties: DroppableProperties) => {
        const [{ isOver }, drop] = useDrop(() => ({
            accept: properties.accept || 'BOX',
            drop: properties.onDrop,
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop()
            })
        }));

        return <div ref={drop} className={cn('rounded-sm', isOver && 'bg-accent')}>
            {properties && properties.children
                ?
                (properties.children)
                :
                <></>
            }
        </div>;
    }

    const partnerColumns: ColumnDef<any>[] = [{
        id: 'delete',
        header: '',
        size: 60,
        cell: ({ row }) => <Button
            variant="ghost"
            size="icon"
            disabled={!ownsSelectedProject()}
            aria-label={t('global.delete')}
            onClick={() => {
                showDeleteDialog('Partner löschen', `${legalPersonCompanyName(row.original.supplier)}`, async () => {
                    setPartnerships(partnerships.filter((entry: any) => entry._id !== row.original._id));
                });
            }}>
            <Trash2 className="h-4 w-4" aria-hidden />
        </Button>,
    }, {
        id: 'partner',
        accessorFn: (partnership: any) => legalPersonCompanyName(partnership.supplier),
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.partner')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => <span className="break-words">{legalPersonCompanyName(row.original.supplier)}</span>,
    }, {
        id: 'address',
        accessorFn: (partnership: any) => legalPersonStreetLine(partnership.supplier),
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.address')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => <div className="break-words">{legalPersonStreetLine(row.original.supplier)}, {legalPersonCityLine(row.original.supplier)}</div>,
    }, {
        id: 'primaryContact',
        accessorFn: (partnership: any) => partnership.emailPrimaryContact,
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.primaryContact')}</SortableHeader>,
        size: 200,
        cell: ({ row }) => <a
            className="break-all text-primary underline-offset-4 hover:underline"
            href={`mailto:${row.original.emailPrimaryContact}`}
        >{row.original.emailPrimaryContact}</a>,
    }, {
        id: 'addedBy',
        header: plainHeader(t('settingsPanel.projectsPanel.addedBy')),
        size: 200,
        cell: () => <div></div>,
    }];

    const siteColumns: ColumnDef<any>[] = [{
        id: 'address',
        accessorFn: (site: any) => site.street,
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.address')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => <div className="break-words">{row.original.street} {row.original.streetNumber}, {row.original.postalCode} {row.original.city}</div>,
    }, {
        id: 'space',
        header: plainHeader(t('settingsPanel.projectsPanel.space')),
        size: 480,
        cell: ({ row }) => {
            const site: any = row.original;

            return <div className="flex min-w-0 flex-col gap-2">
                {(site.buildings ?? []).map((building: Building) => {
                    const be = building as any;
                    const addressLine =
                        buildingFullAddressLine(be) ||
                        [buildingStreetLine(site), buildingPostalCityLine(site)].filter(Boolean).join(', ');
                    return (
                        <div key={be._id ?? be.id} className="flex min-w-0 flex-col">
                            {addressLine ? (
                                <span className="block break-words text-sm leading-tight text-muted-foreground">
                                    {addressLine}
                                </span>
                            ) : null}
                            {/* Der Weg ins Gebäude ist die Hauptaktion der Zeile — als beschrifteter
                                Knopf, nicht als Symbol für einen externen Link hinter „Ganzes Gebäude (-)". */}
                            <div className="flex flex-wrap items-center gap-2 text-sm leading-tight">
                                <span>
                                    {t('settingsPanel.projectsPanel.wholeBuilding')}
                                    {be.id_osm ? <span className="text-muted-foreground"> ({be.id_osm})</span> : null}
                                </span>
                                {entityHref('Building', be) ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 gap-1"
                                        aria-label={`${t('entityPage.showBuilding')}: ${addressLine || t('settingsPanel.projectsPanel.wholeBuilding')}`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const href = entityHref('Building', be);
                                            if (href) navigate(href);
                                        }}
                                    >
                                        {t('entityPage.showBuilding')}
                                        <ChevronRight className="h-4 w-4" aria-hidden />
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    );
                })}

                {/* Nothing to show means the site has no building — either because the ensure call
                    failed when it was created, or because it predates the link being persisted.
                    Both are repairable from here, which is why this offers the repair rather than
                    an empty cell: the building is the only way to floor plans and equipment. */}
                {(site.buildings ?? []).length === 0 ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="self-start"
                        disabled={linkingSiteId === entityIdAsString(site._id)}
                        onClick={(event) => {
                            event.stopPropagation();
                            void linkBuildingForSite(site);
                        }}
                    >
                        {linkingSiteId === entityIdAsString(site._id)
                            ? t('settingsPanel.projectsPanel.linkingBuilding')
                            : t('settingsPanel.projectsPanel.linkBuilding')}
                    </Button>
                ) : null}
            </div>;
        },
    }, {
        // Löschen als letzte Spalte ganz rechts (Muster der Projektliste).
        id: 'delete',
        header: '',
        size: 60,
        cell: ({ row }) => <div className="flex justify-end"><Button
            variant="ghost"
            size="icon"
            disabled={!ownsSelectedProject()}
            aria-label={t('global.delete')}
            onClick={(event) => {
                event.stopPropagation();
                showDeleteDialog('Liegenschaft löschen', `${row.original.street} ${row.original.streetNumber}, ${row.original.postalCode} ${row.original.city}`, async () => {
                    setSites(sites.filter((checkSite: Site) => checkSite._id !== row.original._id));
                });
            }}>
            <Trash2 className="h-4 w-4" aria-hidden />
        </Button></div>,
    }];

    /**
     * Give a site its building, after the fact.
     *
     * Every site created before the link was persisted — and every one whose ensure call failed
     * silently — sits in the table with an empty cell and no way into its building. This is the way
     * back: ask the server for the building of that address, record the link, and show it.
     */
    const linkBuildingForSite = async (site: any) => {
        const siteId = entityIdAsString(site._id);
        if (!siteId) return;

        setLinkingSiteId(siteId);

        try {
            const buildings = await ensureBuildingsForSite(site, siteId);

            if (buildings.length === 0) {
                pushError(new Error(t('projectPanel.noBuildingForAddress')));
                return;
            }

            await userAPI.updateSite(authentication.customer, {
                ...site,
                buildings: buildings
                    .map((building: any) => entityIdAsString(building?._id))
                    .filter((id: string | undefined): id is string => !!id),
            } as any);

            setSites((current: any[]) =>
                current.map((entry: any) =>
                    entityIdAsString(entry._id) === siteId ? { ...entry, buildings } : entry,
                ),
            );
        } catch (error) {
            pushError(error);
        } finally {
            setLinkingSiteId(null);
        }
    };

    const selectProject = useCallback(async (project: any) => {
        if (!project) {
            setSelectedProject(null);
            setProjectModified(false);
            setLoadingSites(false);
            return;
        }

        setSelectedProject(project);
        setProjectModified(false);

        // TODO All graph traversal should be done server-side via mongoose 8 as long as it is in

        setLoadingSites(true);

        try {
            const siteIds = (Array.isArray(project.sites) ? project.sites : [])
                .map((s: unknown) => entityIdAsString(s))
                .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);

            const newSites: any[] = [];

            for (const siteId of siteIds) {
                try {
                    const newSite: any = await withTimeoutPromise(
                        userAPI.queryOne(from(Site.type).where(is('_id').equal(siteId))),
                        PROJECT_SITE_QUERY_TIMEOUT_MS,
                        null,
                    );

                    if (!newSite) {
                        continue;
                    }

                    // What the site has recorded comes first; matching by address is the fallback
                    // for sites written before the link was persisted. The other way round, a
                    // repaired site went empty again on the next reload. One query for the recorded
                    // ids rather than one per building, and the same helper serves the archive
                    // scope below, which would otherwise keep matching on addresses.
                    newSite.buildings = await withTimeoutPromise(
                        buildingsForSite(newSite),
                        BUILDING_QUERY_TIMEOUT_MS,
                        [],
                    );

                    newSites.push(newSite);
                } catch (siteErr) {
                    console.error('Failed to load site', siteId, siteErr);
                }
            }

            setSites(newSites);
        } catch (error) {
            pushError(error);
            setSites([]);
        } finally {
            setLoadingSites(false);
        }

        try {
            const partnershipIds = (Array.isArray(project.partnerships) ? project.partnerships : [])
                .map((p: unknown) => entityIdAsString(p))
                .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);

            const newPartnerships: any[] = [];

            for (const partnershipId of partnershipIds) {
                try {
                    const newPartnership: any = await userAPI.queryOne(from(Partnership.type).where(is('_id').equal(partnershipId)));

                    if (!newPartnership) {
                        continue;
                    }

                    const customerId = entityIdAsString(newPartnership.customer);
                    const supplierId = entityIdAsString(newPartnership.supplier);

                    if (customerId) {
                        newPartnership.customer = await userAPI.queryOne(from(LegalPerson.type).where(is('_id').equal(customerId)));
                    }
                    if (supplierId) {
                        newPartnership.supplier = await userAPI.queryOne(from(LegalPerson.type).where(is('_id').equal(supplierId)));
                    }

                    newPartnerships.push(newPartnership);
                } catch (pErr) {
                    console.error('Failed to load partnership', partnershipId, pErr);
                }
            }

            setPartnerships(newPartnerships);
            setPartnerTreeData(buildPartnerTree(newPartnerships));

            const pid = entityIdAsString(project._id);
            if (pid) {
                // Aus dem aktuellen Stand, nicht aus dem des Renderdurchlaufs — und ohne `searchParams`
                // oder `setSearchParams` in den Abhängigkeiten, sonst wird `selectProject` bei jedem
                // Blättern neu gebaut und stößt darüber den Projekte-Loader an.
                setSearchParamsRef.current((current) => {
                    const nextSearchParams = new URLSearchParams(current);
                    nextSearchParams.set('project', pid);
                    return nextSearchParams;
                });
            }
        } catch (error) {
            pushError(error);
            setPartnerships([]);
            setPartnerTreeData(buildPartnerTree([]));
        }
    }, [buildPartnerTree, pushError]);

    /** Neues, noch nicht gespeichertes Projekt anlegen (Schaltfläche und Leerzustand der Tabelle). */
    const startNewProject = () => {
        setSelectedProject(new Project({ name: `Project ${new Date().toLocaleDateString()}`, startDate: new Date() }));
        setPartnerships([]);
        setSites([]);
        setClient(null);
    };

    /**
     * Prefill from the address (`?panel=projects&guide=settings.projects.create&name=…&description=…`):
     * the same as `startNewProject`, just with name and description from the address instead of
     * the default name. Client and start date remain a selection and the default (today), respectively.
     */
    useGuideAction(
        'settings.projects.create',
        (values) => {
            setSelectedProject(new Project({
                name: values.name ?? `Project ${new Date().toLocaleDateString()}`,
                description: values.description,
                startDate: new Date(),
            }));
            setPartnerships([]);
            setSites([]);
            setClient(null);
        },
        { panel: 'projects' },
    );

    /** Zeilenklick und Namens-Button öffnen dasselbe Projekt. */
    const openProject = (project: any) => {
        setClient(project._company);
        void selectProject(project);
    };

    const columns: ColumnDef<any>[] = [{
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.name')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => (
            <div className="min-w-0">
                {/* Eigener Fokusstop: die klickbare Zeile allein wäre per
                    Tastatur nicht erreichbar. */}
                <button
                    type="button"
                    className="break-words text-left font-medium underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                    aria-label={t('settingsPanel.projectsPanel.editProject')}
                    onClick={(event) => {
                        event.stopPropagation();
                        openProject(row.original);
                    }}
                >
                    {row.original.name}
                </button>
                {/* Auf schmalen Screens rücken die ausgeblendeten Spalten als
                    Unterzeile hierher. */}
                <div className="mt-0.5 flex flex-wrap items-center gap-2 sm:hidden">
                    {authentication.customer === row.original._tenant
                        ? <Badge variant="secondary">EIGENTÜMER</Badge>
                        : <Badge variant="outline">PARTNER</Badge>}
                </div>
                {row.original._company ? (
                    <div className="mt-0.5 text-xs text-muted-foreground md:hidden">
                        {legalPersonCompanyName(row.original._company)}
                    </div>
                ) : null}
            </div>
        ),
    }, {
        id: 'ownership',
        accessorFn: (project: any) => project._tenant,
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.ownership')}</SortableHeader>,
        size: 130,
        meta: { className: 'hidden sm:table-cell' },
        // Eigentümer/Partner als Badge auf Token-Farben: die frühere Kundenfarbe
        // erreichte auf weißem Grund keinen ausreichenden Textkontrast.
        cell: ({ row }) => (authentication.customer === row.original._tenant ?
            <Badge variant="secondary">EIGENTÜMER</Badge>
            :
            <Badge variant="outline">PARTNER</Badge>),
    }, {
        id: 'customer',
        accessorFn: (project: any) => (project._company ? legalPersonCompanyName(project._company) : ''),
        header: ({ column }) => <SortableHeader column={column}>{t('settingsPanel.projectsPanel.customer')}</SortableHeader>,
        size: 300,
        meta: { className: 'hidden md:table-cell' },
        cell: ({ row }) => row.original._company ? <div className="min-w-0 break-words">
            <div>{legalPersonCompanyName(row.original._company)}</div>
            <div>{legalPersonAddressFields(row.original._company).city}</div>
        </div> : <span></span>,
    }, {
        id: 'objects',
        header: plainHeader(t('settingsPanel.projectsPanel.objects')),
        size: 300,
        meta: { className: 'hidden lg:table-cell' },
        cell: () => <span></span>,
    }, {
        // Löschen bewusst als letzte Spalte ganz rechts in der Zeile.
        id: 'delete',
        header: '',
        size: 60,
        cell: ({ row }) => <div className="flex justify-end"><Button
            variant="ghost"
            size="icon"
            aria-label={t('global.delete')}
            onClick={async (event) => {
                event.stopPropagation();
                showDeleteDialog('Projekt löschen', row.original.name, async () => {
                    try {
                        await deleteProject(row.original);
                        forgetProjects(authentication.customer);

                        setProjects(projects.filter((otherProject: any) => otherProject._id !== row.original._id));
                    } catch (error) {
                        pushError(error);
                    }
                });
            }}>
            <Trash2 className="h-4 w-4" aria-hidden />
        </Button></div>,
    }];

    // TODO Needed?

    useEffect(() => {
        if (!selectedSite) {
            return;
        }

        const call = async () => {
            refreshRoles(sites);
        };

        call();
    }, [refreshRoles, selectedSite, sites]);

    useEffect(() => {
        if (selectedProject && selectedProject._id) {
            setLoadingEntitlements(true);

            const call = async () => {
                try {
                    const newProjectOwner = await userAPI.queryOne(from(Customer.type).where(is('id').equal(selectedProject._tenant)));

                    setProjectOwner(newProjectOwner);

                    const documentMap: any = {};

                    for (const id of documentIdsOf(selectedProject.documents)) {
                        const document = await userAPI.queryOne(from(DocumentDetails.type).where(is('_id').equal(id)));

                        console.log('Document >>>', id, document);

                        // @ts-ignore
                        documentMap[document.url] = { ...document };
                    }

                    setDocuments(Object.values(documentMap));
                    refreshRoles(sites);

                    // RESTfulness

                    setSelectedSite(sites.find((site: any) => site._id === siteIdInUrl));

                    // Retrieve an order to check, whether billing can be done for this project

                    const newOrder = await userAPI.fullOrderForProject(authentication.customer, selectedProject._id as any);

                    console.log('Order >>>', newOrder);

                    setOrder(newOrder);
                } catch (error) {
                    pushError(error);
                } finally {
                    setLoadingEntitlements(false);
                }
            };

            call();
        } else {
            setLoadingProjects(true);

            const call = async () => {
                try {
                    if (!authentication?.customer) {
                        setProjects([]);
                        return;
                    }

                    const projects = await userAPI.query(
                        from(Project.type).where(is('_tenant').equal(authentication.customer)),
                    );

                    console.log('Projects loaded >>>', { tenant: authentication.customer, count: projects?.length, names: (projects || []).map((p: any) => p.name) });

                    for (const project of projects) {
                        if (project.customer) {
                            // Best-effort: a failing company lookup must not blank the whole project list.
                            // Address fields live top-level on LegalPerson — the legacy
                            // `headquarters` link is no longer needed (legalPersonAddressFields
                            // falls back to it for old rows).
                            try {
                                const company = await userAPI.queryOne(
                                    from(LegalPerson.type).where(is('_id').equal(project.customer)));
                                project._company = company;
                            } catch (companyError) {
                                console.warn('Company lookup failed for project >>>', project.name, companyError);
                            }
                        }
                    }

                    setProjects(projects);

                    if (projectIdInUrl) {
                        const match = projects.find(
                            (p: any) => entityIdAsString(p._id) === projectIdInUrl
                        );
                        await selectProject(match);
                    }
                } catch (error) {
                    pushError(error);
                } finally {
                    setLoadingProjects(false);
                }
            };

            call();
        }
    }, [authentication.customer, projectIdInUrl, pushError, refreshRoles, selectProject, selectedProject, siteIdInUrl, sites]);

    const setTabParam = (value: string) => {
        searchParams.set('tab', value);
        setSearchParams(searchParams);
        setTab(value);
    }

    const setSiteParam = (id: any) => {
        searchParams.set('site', id);
        setSearchParams(searchParams);

    }

    /** Zeilenklick wählt die Liegenschaft aus (ersetzt die Auswahlspalte der alten Tabelle). */
    const toggleSiteSelection = (site: any) => {
        if (selectedSite && selectedSite._id === site._id) {
            setSelectedSite(null);
            return;
        }

        setSelectedSite(site);
        setSiteParam(site._id);
    };

    const isValidEmail = (email: string) => {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    const isValid = () => {
        return selectedProject.name && selectedProject.name.trim().length > 0 &&
            selectedProject.description && selectedProject.description.trim().length > 0 &&
            selectedProject.startDate &&
            selectedProject.customer;
    };

    /** Patch the selected project and flag it dirty so the Save button enables. */
    const editProject = (patch: Record<string, any>) => {
        setSelectedProject((prev: any) => ({ ...prev, ...patch }));
        setProjectModified(true);
    };

    const isNewSupplierValid = () => {
        return newSupplier &&
            emailPrimaryContact && emailPrimaryContact.length > 0 && isValidEmail(emailPrimaryContact);
    }

    /** Gültigkeit der Primärkontakt-Adresse — Grundlage für Fehlertext und Hinzufügen-Schaltfläche. */
    const isNewSupplierEmailValid = () => {
        return !!(emailPrimaryContact && isValidEmail(emailPrimaryContact));
    }

    const ownsSelectedProject = () => {
        return !selectedProject._tenant || selectedProject._tenant === authentication.customer;
    }

    const canAdministrateSelectedProject = () => {
        return ownEntitlement && ownEntitlement.roles.indexOf('administration') >= 0;
    }

    const isBuildingPartComplete = useCallback(() => {
        const addr = address ?? objectSelectionAddressRef.current;
        if (!addr) {
            return false;
        }
        if (buildingPart === BuildingPart.partOfBuilding) {
            return !!(floorName || selectedFloors.length > 0);
        }
        return true;
    }, [address, buildingPart, floorName, selectedFloors]);

    /** Debug: Objektauswahl — if addButtonDisabled is true, the Hinzufügen button will not fire onClick */
    useEffect(() => {
        if (!objectSelectDialogOpen) return;
        console.log('[ProjectPanel Objektauswahl] dialog state', {
            addButtonDisabled: !isBuildingPartComplete(),
            hasAddressState: !!address,
            hasAddressRef: !!objectSelectionAddressRef.current,
            buildingPart,
            street: (address ?? objectSelectionAddressRef.current)?.street,
        });
    }, [objectSelectDialogOpen, address, buildingPart, floorName, isBuildingPartComplete, selectedFloors, siteName]);

    const saveProject = async () => {
        selectedProject.documents = documents.map((document: DocumentDetails) => document._id);
        selectedProject.sites = sites.map((site: Site) => site._id);
        selectedProject.partnerships = partnerships.map((partnership: Partnership) => partnership._id);

        await updateProject(selectedProject);
        forgetProjects(authentication.customer);
    }

    const projectHeaderAddressLine = useMemo(() => {
        const siteFromUrl =
            siteIdInUrl && Array.isArray(sites)
                ? sites.find((s: any) => entityIdAsString(s._id) === siteIdInUrl)
                : null;
        const site =
            selectedSite ??
            siteFromUrl ??
            (Array.isArray(sites) && sites.length === 1 ? sites[0] : null);
        return siteAddressLineForProjectHeader(site);
    }, [selectedSite, sites, siteIdInUrl]);

    const scopedSites = useMemo(() => {
        return (Array.isArray(sites) ? sites : []).filter(
            (site: any) => site && site.street && site.streetNumber && site.postalCode
        );
    }, [sites]);

    const downloadFile = (blob: Blob, fileName: string) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 7000);
    };

    // Download a project-level ZIP: assets.csv plus photos grouped as
    // <City>/<PostalCode>/<Site Address>/<Floor>/<Space>/<Asset>/ for every site.
    const downloadProjectArchive = async () => {
        if (scopedSites.length === 0) {
            pushError(new Error('No sites with complete address information (street, street number, and postal code) to download'));
            return;
        }
        setDownloadingProject(true);
        try {
            const siteFilters = scopedSites.map(siteAddressFilter);
            const projectId = entityIdAsString(selectedProject?._id);
            const siteIds = Array.from(new Set([
                ...(Array.isArray(selectedProject?.sites) ? selectedProject.sites : []).map((site: unknown) => entityIdAsString(site)),
                ...scopedSites.map((site: any) => entityIdAsString(site?._id)),
            ].filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0)));
            const archiveBuildings = await projectArchiveBuildingsForSites(scopedSites);
            const buildingIds = Array.from(new Set(siteBuildingIds([{ buildings: archiveBuildings }])));
            const floorRows = (
                await Promise.all(
                    buildingIds.map((buildingId) =>
                        userAPI.query(from(Floor.type).where(is('building').equal(buildingId))).catch(() => [])
                    )
                )
            ).flat();
            const scope = {
                projectIds: new Set(projectId ? [projectId] : []),
                siteIds: new Set(siteIds),
                buildingIds: new Set(buildingIds),
                floorIds: new Set(
                    floorRows
                        .map((floor: any) => entityIdAsString(floor?._id))
                        .filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0)
                ),
                siteAddressKeys: projectArchiveSiteAddressKeys(scopedSites),
            };
            const candidateFilter = projectArchiveAssetCandidateFilter(siteFilters, scope);
            const candidateAssets = await userAPI.query(
                new Query(
                    new EntityQuery(
                        Asset.type,
                        [],
                        undefined,
                        candidateFilter
                    )
                )
            );
            const assetIds = Array.from(new Set(
                candidateAssets
                    .filter((asset: any) => assetMatchesProjectArchiveScope(asset, scope))
                    .map((asset: any) => entityIdAsString(asset?._id))
                    .filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0)
            ));
            if (assetIds.length === 0) {
                pushError(new Error('No assets linked to the selected project sites or entitlements to download'));
                return;
            }
            const blob = await userAPI.queryZip(
                new Query(
                    new EntityQuery(
                        Asset.type,
                        [],
                        undefined,
                        oneOrManyEqualFilter('_id', assetIds)
                    )
                ),
                { grouped: true }
            );
            const name = (selectedProject?.name || 'project').replace(/[/\\:*?"<>|]+/g, '_');
            downloadFile(blob, `${name}-${new Date().toISOString().replace(/:/g, '-')}.zip`);
        } catch (error) {
            pushError(error);
        } finally {
            setDownloadingProject(false);
        }
    };

    return <><div className="min-w-0">
        {selectedProject
            ?
            <div className="flex min-w-0 flex-col">
                <div className='flex flex-wrap items-center gap-2'>
                    <div className='flex min-w-0 flex-1 flex-col gap-1'>
                        <div className='break-words text-lg font-semibold'>{selectedProject.name}</div>
                        {projectHeaderAddressLine ? (
                            <div className='break-words text-sm text-muted-foreground' role="status">
                                {projectHeaderAddressLine}
                            </div>
                        ) : null}
                    </div>
                    {downloadingProject ? (
                        <div className='flex items-center gap-2'>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            <div className='text-sm'>{t('global.downloading')}</div>
                        </div>
                    ) : (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={scopedSites.length === 0}
                                            aria-label={t('settingsPanel.projectsPanel.downloadProjectArchive')}
                                            onClick={downloadProjectArchive}
                                        >
                                            <Download className="h-4 w-4" aria-hidden />
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>{t('settingsPanel.projectsPanel.downloadProjectArchive')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {ownsSelectedProject() || canAdministrateSelectedProject()
                        ?
                        (selectedProject && selectedProject._id
                            ?
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                disabled={!isValid() || !projectModified}
                                                aria-label={t('global.save')}
                                                onClick={async () => {
                                                    try {
                                                        await saveProject();
                                                        setSelectedProject(null);
                                                        setProjectModified(false);
                                                        searchParams.delete('project');
                                                        setSearchParams(searchParams);
                                                    } catch (error) {
                                                        pushError(error);
                                                    }
                                                }}
                                            >
                                                <Save className="h-4 w-4" aria-hidden />
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('global.save')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            :
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                disabled={!isValid()}
                                                aria-label={t('global.create')}
                                                onClick={async () => {
                                                    try {
                                                        selectedProject.documents = documents.map((document: DocumentDetails) => document._id);
                                                        selectedProject.sites = sites.map((site: Site) => site._id);
                                                        selectedProject.partnerships = partnerships.map((partnership: Partnership) => partnership._id);

                                                        await createProject(selectedProject);
                                                        forgetProjects(authentication.customer);

                                                        setSelectedProject(null);
                                                        setClient(null);
                                                        searchParams.delete('project');
                                                        setSearchParams(searchParams);
                                                        setProjectModified(false);
                                                        pushSuccess(t('settingsPanel.projectsPanel.projectCreated'));
                                                    } catch (error) {
                                                        pushError(error);
                                                    }
                                                }}
                                            >
                                                <Save className="h-4 w-4" aria-hidden />
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('global.create')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )
                        :
                        <></>
                    }
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t('global.close')}
                                    onClick={() => {
                                        setSelectedProject(null);
                                        setProjectModified(false);
                                        searchParams.delete('project');
                                        setSearchParams(searchParams);
                                    }}>
                                    <X className="h-4 w-4" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('global.close')}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <Tabs value={tab} onValueChange={setTabParam} className="mt-6 min-w-0">
                    <TabsList className="max-w-full overflow-x-auto">
                        <TabsTrigger value="project">{t('settingsPanel.projectsPanel.general')}</TabsTrigger>
                        <TabsTrigger value="objects">{t('settingsPanel.projectsPanel.sites')}</TabsTrigger>
                        <TabsTrigger value="documents">{t('settingsPanel.projectsPanel.documents')}</TabsTrigger>
                        {isLoggedInUserEntitled(selectedProject, Entitlement.projectManagement)
                            ? <TabsTrigger value="partners">{t('settingsPanel.projectsPanel.partners')}</TabsTrigger>
                            : null}
                        {isLoggedInUserEntitled(selectedProject, Entitlement.projectManagement)
                            ? <TabsTrigger value="entitlements">{t('settingsPanel.projectsPanel.entitlements')}</TabsTrigger>
                            : null}
                        {order /*&& isLoggedInUserEntitled(selectedProject, 'ProjectBilling')*/
                            ? <TabsTrigger value="invoicing">{t('settingsPanel.projectsPanel.invoicing')}</TabsTrigger>
                            : null}
                    </TabsList>
                    <TabsContent value="project">
                        <div className='mt-5 flex w-full min-w-0 max-w-2xl flex-col gap-4'>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="project-name">
                                    {t('settingsPanel.projectsPanel.name')}
                                    <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                                </Label>
                                <Input
                                    id="project-name"
                                    required
                                    disabled={!ownsSelectedProject()}
                                    value={selectedProject.name ?? ''}
                                    onChange={(event) => editProject({ name: event.target.value })}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="project-description">
                                    {t('settingsPanel.projectsPanel.description')}
                                    <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                                </Label>
                                <Textarea
                                    id="project-description"
                                    required
                                    disabled={!ownsSelectedProject()}
                                    value={selectedProject.description ?? ''}
                                    onChange={(event) => editProject({ description: event.target.value })}
                                />
                            </div>
                            <div className="flex flex-wrap gap-4">
                                <DateField
                                    id="project-start-date"
                                    label={t('settingsPanel.projectsPanel.startDate')}
                                    disabled={!ownsSelectedProject()}
                                    value={selectedProject.startDate ? new Date(selectedProject.startDate) : undefined}
                                    onSelectDate={(date: Date) => editProject({ startDate: new Date(date) })}
                                />
                                <DateField
                                    id="project-end-date"
                                    label={t('settingsPanel.projectsPanel.endDate')}
                                    disabled={!ownsSelectedProject()}
                                    value={selectedProject.endDate ? new Date(selectedProject.endDate) : undefined}
                                    onSelectDate={(date: Date) => editProject({ endDate: new Date(date) })}
                                />
                            </div>
                            <div className="min-w-0">
                                <div className='mt-2 text-lg font-semibold'>{t('settingsPanel.projectsPanel.customer')}</div>
                                {client
                                    ?
                                    <div className='mt-5 flex min-w-0 flex-col gap-0.5 text-sm'>
                                        <div className='font-bold break-words'>{legalPersonCompanyName(client)}</div>
                                        <div className="break-words">{legalPersonStreetLine(client)}</div>
                                        <div className="break-words">{(() => {
                                            const a = legalPersonAddressFields(client);
                                            return [legalPersonCityLine(client), a.country].filter(Boolean).join(', ');
                                        })()}</div>
                                        {client.tradeRegisterId && client.tradeRegisterCity
                                            ?
                                            <div className="break-words">{client.tradeRegisterId}, Handelsregister {client.tradeRegisterCity}</div>
                                            :
                                            <></>
                                        }
                                        <div className="mt-2">
                                            <Button variant="ghost" className="gap-2" disabled={!ownsSelectedProject()} onClick={() => {
                                                setClient(null);
                                                editProject({ customer: undefined });
                                            }}>
                                                <Edit className="h-4 w-4" aria-hidden />
                                                {t('global.change')}
                                            </Button>
                                        </div>
                                    </div>
                                    :
                                    <LegalPersonPicker className='mt-5' setLegalPerson={(legalPerson: any) => {
                                        console.log(legalPerson);
                                        setClient(legalPerson);

                                        if (legalPerson) {
                                            editProject({ customer: legalPerson._id });
                                        } else {
                                            editProject({ customer: undefined });
                                        }
                                    }} />
                                }
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="objects">
                        <div className="flex min-w-0 flex-col gap-4">
                            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                                <div className='text-sm'>{t('settingsPanel.projectsPanel.buildingsSpacesDetails')}:</div>
                                <Button variant="outline" className="gap-2" disabled={!ownsSelectedProject()}
                                    onClick={() => {
                                        objectSelectionAddressRef.current = null;
                                        setAddress(null);
                                        setSiteName('');
                                        setSiteNameTouched(false);
                                        setBuilding(null);
                                        setFloorName('');
                                        setFloorNameTouched(false);
                                        setSelectedFloors([]);
                                        setFloors([]);
                                        setObjectSelectDialogOpen(true);
                                    }}
                                    data-guide-id="settings.projects.addSite">
                                    <Plus className="h-4 w-4" aria-hidden />
                                    {t('settingsPanel.projectsPanel.addSite')}
                                </Button>
                            </div>
                            <DataTable
                                data={sites || []}
                                columns={siteColumns}
                                pageSize={5}
                                page={projectSitesPagination.page}
                                onPageChange={projectSitesPagination.onPageChange}
                                previousAriaLabel={t('global.back')}
                                nextAriaLabel={t('global.next')}
                                loading={loadingSites}
                                onRowClick={toggleSiteSelection}
                                rowClassName={(site: any) =>
                                    selectedSite && selectedSite._id === site._id ? 'bg-accent hover:bg-accent' : undefined}
                                emptyMessage={<TableEmptyState
                                    title={t('settingsPanel.projectsPanel.noSitesInProject')}
                                    hint={t('settingsPanel.projectsPanel.noSitesHint')}
                                />}
                            />
                            {selectedSite
                                ?
                                <div className="min-w-0">
                                    <div className="text-lg font-semibold">{t('settingsPanel.projectsPanel.assets')}</div>
                                    <AssetList className="mt-5" scopeSites={selectedSite ? [selectedSite] : []} showStatus></AssetList>
                                </div>
                                :
                                <></>
                            }
                        </div>
                    </TabsContent>
                    <TabsContent value="documents">
                        {/*
                          * Der Datenraum des Projekts, wie ihn auch das Dashboard zeigt — nur
                          * nebeneinander statt übereinander, weil hier die volle Breite da ist.
                          *
                          * Die abgelöste Liste kannte nur, was über sie selbst hochgeladen worden
                          * war, und wusste nichts von dem, was die Auswertung über eine Datei
                          * festgestellt hat. Zwei Ansichten auf dieselbe Ablage, von denen eine
                          * weniger weiß, sind eine zu viel.
                          */}
                        <ProjectDocumentsTab
                            projectId={selectedProject?._id}
                            projectName={selectedProject?.name}
                        />
                    </TabsContent>
                    {isLoggedInUserEntitled(selectedProject, Entitlement.projectManagement)
                        ?
                        <TabsContent value="partners">
                            <div className="flex min-w-0 flex-col gap-4">
                                <div className='mt-6 text-lg font-semibold'>{t('settingsPanel.projectsPanel.partners')}</div>
                                <div className="flex flex-wrap gap-2">
                                    <Button variant="ghost" className="gap-2" disabled={!ownsSelectedProject() && !canAdministrateSelectedProject()}
                                        onClick={() => {
                                            setSelectedEntitlement(null);
                                            setEmailPrimaryContactTouched(false);
                                            setPartnerSelectDialogOpen(true);
                                        }}
                                        data-guide-id="settings.projects.addPartner">
                                        <Plus className="h-4 w-4" aria-hidden />
                                        {t('settingsPanel.projectsPanel.addPartner')}
                                    </Button>
                                </div>
                                <DataTable
                                    data={partnerships}
                                    columns={partnerColumns}
                                    pageSize={5}
                                    page={projectPartnershipsPagination.page}
                                    onPageChange={projectPartnershipsPagination.onPageChange}
                                    previousAriaLabel={t('global.back')}
                                    nextAriaLabel={t('global.next')}
                                    loading={loadingEntitlements}
                                    emptyMessage={<TableEmptyState
                                        title={t('settingsPanel.projectsPanel.noProjectPartnersDefined')}
                                        hint={t('settingsPanel.projectsPanel.noPartnersHint')}
                                    />}
                                />
                            </div>
                        </TabsContent>
                        :
                        null
                    }
                    {isLoggedInUserEntitled(selectedProject, Entitlement.projectManagement)
                        ?
                        <TabsContent value="entitlements">
                            <div className='mt-5 grid min-w-0 gap-5 md:grid-cols-2 xl:grid-cols-3'>
                                <div className="min-w-0">
                                    <div className='text-sm font-bold'>Rollen/Gewerke</div>
                                    <div className='mt-3 max-h-[600px] overflow-auto rounded-md border'>
                                        <RoleList></RoleList>
                                    </div>
                                </div>
                                <div className="min-w-0">
                                    <div className='text-sm font-bold'>{t('settingsPanel.projectsPanel.entitlements')}</div>
                                    <div className='mt-3 max-h-[600px] overflow-auto rounded-md border'>
                                        <TreeView
                                            data={siteTreeData}
                                            aria-label="Controlled expanded node tree"
                                            expandedIds={expandedIds}
                                            nodeRenderer={({
                                                element: site,
                                                isBranch,
                                                isExpanded,
                                                isDisabled,
                                                getNodeProps,
                                                level,
                                                handleExpand,
                                            }) => {
                                                return (
                                                    <div
                                                        {...getNodeProps({ onClick: handleExpand })}
                                                        className={cn(
                                                            'flex cursor-pointer items-center rounded-sm hover:bg-accent aria-selected:bg-accent aria-selected:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                                            isDisabled && 'opacity-50',
                                                        )}
                                                        style={{
                                                            marginLeft: 40 * (level - 1),
                                                        }}
                                                    >
                                                        {(() => {
                                                            switch (site.metadata?.nodeType) {
                                                                case 'global': return <>
                                                                    <div className='flex w-4 shrink-0 items-center'>
                                                                        {isBranch ?
                                                                            <ArrowIcon isOpen={isExpanded} />
                                                                            :
                                                                            <></>}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Droppable accept={'role'} onDrop={(role: any) => {
                                                                            console.log('Dropping >>>', role, site);

                                                                            // if (expandedIds.indexOf(element.id) < 0) {
                                                                            //     setExpandedIds([...expandedIds, element.id]);
                                                                            // }

                                                                            addRoleToSite(role.metadata, site.metadata);
                                                                        }}>
                                                                            <div className="flex items-center gap-2 p-1">
                                                                                <Globe className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                                                                                <div className='text-sm font-bold'>{site.metadata.name}</div>
                                                                            </div>
                                                                        </Droppable>
                                                                    </div>
                                                                </>;
                                                                case 'project': return <>
                                                                    <div className='flex w-4 shrink-0 items-center'>
                                                                        {isBranch ?
                                                                            <ArrowIcon isOpen={isExpanded} />
                                                                            :
                                                                            <></>}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Droppable accept={'role'} onDrop={(role: any) => {
                                                                            console.log('Dropping >>>', role, site);

                                                                            // if (expandedIds.indexOf(element.id) < 0) {
                                                                            //     setExpandedIds([...expandedIds, element.id]);
                                                                            // }

                                                                            addRoleToProject(role);
                                                                        }}>
                                                                            <div className="flex items-center gap-2 p-1">
                                                                                <CalendarCog className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                                                                                <div className='text-sm font-bold'>{site.metadata.name}</div>
                                                                            </div>
                                                                        </Droppable>
                                                                    </div>
                                                                </>;
                                                                case 'site': return <>
                                                                    <div className='flex w-4 shrink-0 items-center'>
                                                                        {isBranch ?
                                                                            <ArrowIcon isOpen={isExpanded} />
                                                                            :
                                                                            <></>}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Droppable accept={'role'} onDrop={(role: any) => {
                                                                            console.log('Dropping >>>', role, site);

                                                                            // if (expandedIds.indexOf(element.id) < 0) {
                                                                            //     setExpandedIds([...expandedIds, element.id]);
                                                                            // }

                                                                            addRoleToSite(role, site.metadata);
                                                                        }}>
                                                                            <div className="flex items-center gap-2 p-1">
                                                                                <MapPin className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                                                                                <div className='text-sm font-bold'>{site.metadata.name}</div>
                                                                            </div>
                                                                        </Droppable>
                                                                    </div>
                                                                </>;
                                                                case 'partner': return <>
                                                                    <div className='flex w-4 shrink-0 items-center'>
                                                                        {isBranch ?
                                                                            <ArrowIcon isOpen={isExpanded} />
                                                                            :
                                                                            <></>}
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 items-center">
                                                                        <div className="flex min-w-0 flex-1 items-center gap-1 p-1">
                                                                            <UserPlus
                                                                                className="h-5 w-5 shrink-0 text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]"
                                                                                aria-hidden
                                                                            />
                                                                            <div className="min-w-0 break-words text-sm">
                                                                                {(site.metadata.delegate as any).name}
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            aria-label={t('global.delete')}
                                                                            onClick={() => {
                                                                                removeRole(site.metadata);
                                                                            }}>
                                                                            <Trash2 className="h-4 w-4" aria-hidden />
                                                                        </Button>
                                                                    </div>
                                                                </>
                                                                default: return <>
                                                                    <div className='flex w-4 shrink-0 items-center'>
                                                                        {isBranch ?
                                                                            <ArrowIcon isOpen={isExpanded} />
                                                                            :
                                                                            <></>}
                                                                    </div>
                                                                    <div className="flex min-w-0 flex-1 items-center">
                                                                        <Droppable accept={'partner'} onDrop={(item: any) => {
                                                                            // if (expandedIds.indexOf(element.id) < 0) {
                                                                            //     setExpandedIds([...expandedIds, element.id]);
                                                                            // }

                                                                            addPartnerToRole(item, site.metadata);
                                                                        }}>
                                                                            <div className="flex items-center gap-1 p-1">
                                                                                <UserCheck className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                                                                                <div className="min-w-0">
                                                                                    {(() => {
                                                                                        const role = new Role(site.metadata);

                                                                                        return <div>
                                                                                            <div className="break-words font-bold">{role.getRoleName()}</div>
                                                                                        </div>;
                                                                                    })()}
                                                                                </div>
                                                                            </div>
                                                                        </Droppable>
                                                                        {site.metadata?.type !== RoleType.role
                                                                            ?
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                aria-label={t('global.delete')}
                                                                                onClick={() => {
                                                                                    removeRole(site.metadata);
                                                                                }}>
                                                                                <Trash2 className="h-4 w-4" aria-hidden />
                                                                            </Button>
                                                                            :
                                                                            <></>
                                                                        }

                                                                    </div>
                                                                </>;
                                                            }
                                                        })()
                                                        }
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="min-w-0">
                                    <div className='text-sm font-bold'>{t('settingsPanel.projectsPanel.partner')}</div>
                                    <div className='mt-3 max-h-[600px] overflow-auto rounded-md border'>
                                        <TreeView
                                            data={partnerTreeData}
                                            aria-label="Controlled expanded node tree"
                                            expandedIds={expandedIds}
                                            defaultExpandedIds={[1]}
                                            nodeRenderer={({
                                                element,
                                                isBranch,
                                                isExpanded,
                                                isDisabled,
                                                getNodeProps,
                                                level,
                                                handleExpand,
                                            }) => {
                                                return (
                                                    <div
                                                        {...getNodeProps({ onClick: handleExpand })}
                                                        className={cn(
                                                            'flex cursor-pointer rounded-sm hover:bg-accent aria-selected:bg-accent aria-selected:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                                            isDisabled && 'opacity-50',
                                                        )}
                                                        style={{
                                                            marginLeft: 40 * (level - 1),
                                                        }}
                                                    >
                                                        {isBranch && <ArrowIcon isOpen={isExpanded} />}
                                                        <Draggable type={'partner'} item={element.metadata}>
                                                            <div className="flex items-center gap-5 p-3">
                                                                <Users
                                                                    className="h-5 w-5 shrink-0 text-[color:var(--customer-secondary,hsl(var(--foreground)))]"
                                                                    aria-hidden
                                                                />
                                                                <div className="min-w-0">
                                                                    <div className="break-words font-bold">{element.name}</div>
                                                                    <div className='mt-1 break-words text-xs text-muted-foreground'>{truncate((element.metadata as any).supplier.purpose, 150)}</div>
                                                                    <div className='mt-2 break-words text-xs'>{legalPersonStreetLine((element.metadata as any).supplier)}, {legalPersonCityLine((element.metadata as any).supplier)}</div>
                                                                    {/* Platzhalter-Bewertung ohne gespeicherten Wert — daher rein dekorativ. */}
                                                                    <div className="mt-1 flex items-center gap-0.5" aria-hidden>
                                                                        {Array.from({ length: 5 }, (_, index) => (
                                                                            <Star
                                                                                key={index}
                                                                                strokeWidth={1.5}
                                                                                className={cn(
                                                                                    'h-4 w-4',
                                                                                    index < new Date().getTime() % 4
                                                                                        ? 'fill-current text-warning'
                                                                                        : 'text-muted-foreground',
                                                                                )}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Draggable>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                        :
                        null
                    }
                    {order /*&& isLoggedInUserEntitled(selectedProject, 'ProjectBilling')*/
                        ?
                        <TabsContent value="invoicing">
                            <div className='mt-5 min-w-0'>
                                <InvoicePanel project={selectedProject} projectChanged={setSelectedProject}></InvoicePanel>
                            </div>
                        </TabsContent>
                        :
                        null
                    }
                </Tabs>
            </div>
            :
            <div className="flex min-w-0 flex-col gap-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className='text-lg font-semibold'>
                        {t('settingsPanel.projectsPanel.projects')}
                    </div>
                    <Button data-guide-id="settings.projects.create" variant="outline" className="gap-2" onClick={startNewProject}>
                        <FolderPlus className="h-4 w-4" aria-hidden />
                        {t('settingsPanel.projectsPanel.createProject')}
                    </Button>
                </div>
                <DataTable
                    data={projects}
                    columns={columns}
                    onRowClick={openProject}
                    pageSize={5}
                    page={projectListPagination.page}
                    onPageChange={projectListPagination.onPageChange}
                    previousAriaLabel={t('global.back')}
                    nextAriaLabel={t('global.next')}
                    loading={loadingProjects}
                    emptyMessage={<TableEmptyState
                        title={t('settingsPanel.projectsPanel.noProjects')}
                        hint={t('settingsPanel.projectsPanel.noProjectsHint')}
                        action={<Button data-guide-id="settings.projects.create" variant="outline" className="gap-2" onClick={startNewProject}>
                            <FolderPlus className="h-4 w-4" aria-hidden />
                            {t('settingsPanel.projectsPanel.createProject')}
                        </Button>}
                    />}
                />
            </div>
        }
    </div>
        <Dialog open={objectSelectDialogOpen} onOpenChange={setObjectSelectDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.projectsPanel.objectSelection')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-col gap-4">
                    <div role="group" aria-labelledby="object-select-address-label" className="flex flex-col gap-1.5">
                        <Label id="object-select-address-label">{t('settingsPanel.projectsPanel.addressBuilding')}</Label>
                        <AddressPicker setAddress={async (address: any) => {
                            objectSelectionAddressRef.current = address;
                            setAddress(address);

                            if (!address) {
                                setBuilding(null);
                                setFloors([]);
                                return;
                            }

                            setSiteName(`${address.street}, ${address.city}`);
                            const buildingsAtAddress = await queryBuildingsForSite(address);
                            const newBuilding = buildingsAtAddress[0] ?? null;

                            if (newBuilding) {
                                setBuilding(newBuilding);
                                const floors = await userAPI.query(from(Floor.type).where(is("building").equal(newBuilding._id)));
                                setFloors(floors);
                            } else {
                                setBuilding(null);
                                setFloors([]);
                            }
                        }} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="object-select-site-name">{t('settingsPanel.projectsPanel.siteName')}</Label>
                        <Input
                            id="object-select-site-name"
                            value={siteName}
                            aria-invalid={siteNameTouched && !siteName ? true : undefined}
                            aria-describedby={siteNameTouched && !siteName ? 'object-select-site-name-error' : undefined}
                            onChange={(event) => setSiteName(event.target.value)}
                            onBlur={() => setSiteNameTouched(true)}
                        />
                        {siteNameTouched && !siteName
                            ? <div id="object-select-site-name-error" className="text-sm text-destructive">
                                {t('settingsPanel.projectsPanel.enterSiteName')}
                            </div>
                            : null}
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label id="object-select-building-part-label">{t('settingsPanel.projectsPanel.space')}</Label>
                        <RadioGroup
                            aria-labelledby="object-select-building-part-label"
                            disabled={!address}
                            value={buildingPart}
                            onValueChange={(value) => setBuildingPart(value)}
                        >
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value={BuildingPart.wholeBuilding} id="object-select-whole-building" />
                                <Label htmlFor="object-select-whole-building" className="font-normal">
                                    {t('settingsPanel.projectsPanel.wholeBuilding')}
                                </Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value={BuildingPart.partOfBuilding} id="object-select-part-of-building" />
                                <Label htmlFor="object-select-part-of-building" className="font-normal">
                                    {t('settingsPanel.projectsPanel.partOfBuilding')}
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>
                    {buildingPart === BuildingPart.wholeBuilding
                        ?
                        <></>
                        :
                        <>
                            {floors.length > 0
                                ?
                                <div className="flex flex-col gap-1.5">
                                    <Label id="object-select-floors-label">{t('settingsPanel.projectsPanel.floor')}</Label>
                                    <Popover open={floorSelectOpen} onOpenChange={setFloorSelectOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                role="combobox"
                                                aria-labelledby="object-select-floors-label"
                                                aria-expanded={floorSelectOpen}
                                                disabled={!address}
                                                className="w-full justify-between font-normal"
                                            >
                                                <span className={cn('truncate', selectedFloors.length === 0 && 'text-muted-foreground')}>
                                                    {selectedFloors.length === 0
                                                        ? t('global.pleaseSelect')
                                                        : selectedFloors.map((floor: any) => floor.name).join(', ')}
                                                </span>
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                            <Command>
                                                <CommandList>
                                                    <CommandEmpty>{t('settingsPanel.projectsPanel.noObjectsDefined')}</CommandEmpty>
                                                    <CommandGroup>
                                                        {floors.map((floor: any) => {
                                                            const checked = !!selectedFloors.find((selected: any) => selected._id === floor._id);

                                                            return <CommandItem
                                                                key={floor._id}
                                                                value={floor.name}
                                                                className="gap-2"
                                                                onSelect={() => {
                                                                    if (selectedFloors.find((selected: any) => selected._id === floor._id)) {
                                                                        setSelectedFloors(selectedFloors.filter((selected: any) => selected._id !== floor._id));
                                                                    } else {
                                                                        setSelectedFloors([...selectedFloors, floors.find((entry: any) => entry._id === floor._id)]);
                                                                    }

                                                                    console.log('Selected floors >>>', selectedFloors);
                                                                }}
                                                            >
                                                                <Checkbox checked={checked} tabIndex={-1} className="pointer-events-none" />
                                                                <span>{floor.name}</span>
                                                            </CommandItem>;
                                                        })}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                :
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="object-select-floor-name">{t('settingsPanel.projectsPanel.floorName')}</Label>
                                    <Input
                                        id="object-select-floor-name"
                                        value={floorName}
                                        aria-invalid={floorNameTouched && !floorName ? true : undefined}
                                        aria-describedby={floorNameTouched && !floorName ? 'object-select-floor-name-error' : undefined}
                                        onChange={(event) => setFloorName(event.target.value)}
                                        onBlur={() => setFloorNameTouched(true)}
                                    />
                                    {floorNameTouched && !floorName
                                        ? <div id="object-select-floor-name-error" className="text-sm text-destructive">
                                            {t('settingsPanel.projectsPanel.enterFloorName')}
                                        </div>
                                        : null}
                                </div>
                            }
                        </>
                    }
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button type="button" variant="outline" onClick={() => {
                        setObjectSelectDialogOpen(false);
                    }}>
                        {t('global.cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={!isBuildingPartComplete()}
                        onClick={async (ev: any) => {
                            console.log('[ProjectPanel Objektauswahl] Hinzufügen onClick fired', {
                                type: ev?.type,
                                buildingPart,
                                hasAddressState: !!address,
                                hasAddressRef: !!objectSelectionAddressRef.current,
                                isBuildingPartComplete: isBuildingPartComplete(),
                            });
                            ev?.preventDefault();
                            ev?.stopPropagation();
                            try {
                                if (buildingPart === BuildingPart.partOfBuilding) {
                                    console.log('[ProjectPanel Objektauswahl] early exit: partOfBuilding not implemented');
                                    pushMessage(t('settingsPanel.projectsPanel.partOfBuildingNotImplemented'));
                                    return;
                                }

                                const addr = address ?? objectSelectionAddressRef.current;
                                if (!addr?.street) {
                                    console.log('[ProjectPanel Objektauswahl] early exit: no street on addr', { addr });
                                    pushMessage(t('settingsPanel.projectsPanel.selectAddressFirst'));
                                    return;
                                }

                                console.log('[ProjectPanel Objektauswahl] proceeding to create/link site', { siteName, addr });

                                let newSite: any = await userAPI.queryOne(new Query(new EntityQuery(Site.type, [], undefined,
                                    and(is('street').equal(addr.street), is('streetNumber').equal(addr.streetNumber), is('postalCode').equal(addr.postalCode), is('city').equal(addr.city), is('country').equal(addr.country)))));

                                if (!newSite) {
                                    newSite = await userAPI.createSite(authentication.customer, new Site({ name: siteName, street: addr.street, streetNumber: addr.streetNumber, postalCode: addr.postalCode, city: addr.city, country: addr.country }));
                                }

                                /** Server used to return {} from POST /sites — client then had no _id for project.sites */
                                if (!entityIdAsString(newSite?._id)) {
                                    newSite = await userAPI.queryOne(new Query(new EntityQuery(Site.type, [], undefined,
                                        and(is('street').equal(addr.street), is('streetNumber').equal(addr.streetNumber), is('postalCode').equal(addr.postalCode), is('city').equal(addr.city), is('country').equal(addr.country)))));
                                }
                                const siteIdStr = entityIdAsString(newSite?._id);
                                if (!siteIdStr) {
                                    pushError(new Error('Site was created but has no id; cannot link to project.'));
                                    return;
                                }
                                newSite = { ...newSite, _id: siteIdStr };

                                newSite.buildings = await ensureBuildingsForSite(
                                    {
                                        ...newSite,
                                        lat: addr.lat,
                                        lon: addr.lon,
                                    },
                                    siteIdStr,
                                );

                                // A site without a building is a dead end: the "Objekt/Fläche" cell
                                // is the only way into the building, and floor plans and equipment
                                // live there. Say so instead of closing the dialog on an empty row.
                                if (newSite.buildings.length === 0) {
                                    pushError(new Error(t('projectPanel.noBuildingForAddress')));
                                    return;
                                }

                                // Persist the link. Without this the connection exists only in this
                                // component's state, and the next reload re-derives it by comparing
                                // address strings — which fails on "Straße" against "Str." and
                                // leaves the site looking empty.
                                try {
                                    await userAPI.updateSite(authentication.customer, {
                                        ...newSite,
                                        buildings: newSite.buildings
                                            .map((building: any) => entityIdAsString(building?._id))
                                            .filter((id: string | undefined): id is string => !!id),
                                    } as any);
                                } catch (linkError) {
                                    // The site and the building exist; only the link failed. Worth
                                    // reporting, not worth discarding the rest over.
                                    console.error('Could not persist the site-building link', linkError);
                                    pushError(linkError);
                                }

                                const nextSites = [...sites, newSite];
                                const siteIds = nextSites
                                    .map((site: Site) => entityIdAsString(site._id))
                                    .filter((id: string | undefined): id is string => typeof id === 'string' && id.length > 0);

                                if (selectedProject._id) {
                                    await updateProject({ ...selectedProject, sites: siteIds });
                                    forgetProjects(authentication.customer);
                                }

                                setSelectedProject({ ...selectedProject, sites: siteIds });
                                setSites(nextSites);
                                await refreshRoles(nextSites);
                                console.log('[ProjectPanel Objektauswahl] success, closing dialog');
                                setObjectSelectDialogOpen(false);
                            } catch (error) {
                                console.error('[ProjectPanel Objektauswahl] Hinzufügen error', error);
                                pushError(error);
                            }
                        }}
                    >
                        {t('global.add')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Dialog open={partnerSelectDialogOpen} onOpenChange={setPartnerSelectDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.projectsPanel.selectPartner')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-col gap-4">
                    <div role="group" aria-labelledby="partner-select-company-label" className="flex flex-col gap-1.5">
                        <Label id="partner-select-company-label">{t('settingsPanel.projectsPanel.company')}</Label>
                        <LegalPersonPicker setLegalPerson={(legalPerson: any) => setNewSupplier(legalPerson)} />
                    </div>
                    <div>
                        <div className='text-lg font-semibold'>
                            {t('settingsPanel.projectsPanel.primaryContact')}
                        </div>
                        <div className='mt-3 text-sm text-muted-foreground'>
                            {t('settingsPanel.projectsPanel.primaryContactDetails')}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="partner-select-email">
                            {t('settingsPanel.projectsPanel.primaryContactEmail')}
                            <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                        </Label>
                        <Input
                            id="partner-select-email"
                            type="email"
                            required
                            value={emailPrimaryContact}
                            aria-invalid={emailPrimaryContactTouched && !isNewSupplierEmailValid() ? true : undefined}
                            aria-describedby={emailPrimaryContactTouched && !isNewSupplierEmailValid() ? 'partner-select-email-error' : undefined}
                            onChange={(event) => setEmailPrimaryContact(event.target.value)}
                            onBlur={() => setEmailPrimaryContactTouched(true)}
                        />
                        {emailPrimaryContactTouched && !isNewSupplierEmailValid()
                            ? <div id="partner-select-email-error" className="text-sm text-destructive">
                                {t('settingsPanel.projectsPanel.enterValidEmail')}
                            </div>
                            : null}
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button type="button" variant="outline" onClick={() => {
                        setPartnerSelectDialogOpen(false);
                    }}>
                        {t('global.cancel')}
                    </Button>
                    <Button type="button" disabled={!newSupplier || !isNewSupplierValid()} onClick={async () => {
                        const customer: any = await userAPI.queryOne(from(Customer.type).where(is('id').equal(authentication.customer)));
                        const legalPerson: any = await userAPI.queryOne(from(LegalPerson.type).where(is('_id').equal(customer.legalPerson)));
                        let newPartnership = await userAPI.queryOne(from(Partnership.type).where(and(is('customer').equal(legalPerson._id), is('supplier').equal(newSupplier._id))));

                        if (!newPartnership) {
                            newPartnership = await userAPI.createPartnership(authentication.customer, new Partnership({
                                customer: legalPerson._id,
                                supplier: newSupplier._id,
                                emailPrimaryContact,
                            }));
                        }

                        // TODO Patch should be done via relationships on server

                        // @ts-ignore
                        newPartnership.customer = legalPerson;
                        // @ts-ignore
                        newPartnership.supplier = newSupplier;

                        setPartnerships([...partnerships, newPartnership]);
                        setNewSupplier(null);
                        setPartnerSelectDialogOpen(false);
                    }}>
                        {t('global.add')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>;
}

const ArrowIcon = ({ isOpen, className }: { isOpen: boolean, className?: string }) => (
    <ChevronRight
        className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            isOpen && 'rotate-90',
            className,
        )}
        aria-hidden
    />
);
